import { acceptHMRUpdate, defineStore } from 'pinia'
import { toRaw } from 'vue'
import { useMaps } from './maps'
import { useDrawingStore } from './drawing'
import * as turf from '@turf/turf'
import { postSplitField, postUploadShapeFileLine } from '../scripts/fieldsPlanning'
import { geoJsonRingToOpenPath } from '@/helpers/geometryHelper'
import { downloadFeatureAsShapefile } from '../scripts/shapefileExport'
import moment from 'moment'

export const useSplitFieldStore = defineStore('splitField', {
    state: () => ({
        isSplitting: false, // Flag to indicate if the field is being split
        drawSplitLine: false, // Flag to indicate if the split line is being drawn
        splitLine: null, // Google Maps Polyline for the split line
        terraDrawListener: null,
        splitLineLength: 0,
        polygons: [], // Array of all polygons from the split response
        polygonAreas: [], // Array of areas for each polygon
        splitPolygons: [], // Array of Google Maps Polygon objects drawn on the map
        noSplitedFieldWarning: null,
        selectedPartIndex: null,
    }),
    actions: {
        splitField() {
            this.clearPreviousDrawing()
            this.drawSplitLine = true
            this.startDrawingSplitLine()
        },
        startDrawingSplitLine() {
            const map = useMaps()
            const ds = useDrawingStore()
            
            if (!map.terraDraw) {
                console.error('TerraDraw not initialized')
                return
            }

            // Cleanup existing listener if any
            this.cleanupDrawingListener()

            // Cleanup drawing store listener to avoid conflicts
            ds.cleanupDrawingListener()
            
            // Start TerraDraw in linestring mode
            map.terraDraw.start()
            map.terraDraw.setMode('linestring')
            
            // Setup listener for drawing completion
            this.setupDrawingListener()
        },
        setupDrawingListener() {
            const map = useMaps()
            const ds = useDrawingStore()
            
            this.terraDrawListener = (id) => {
                const features = map.terraDraw.getSnapshot()
                const feature = features.find(f => f.id === id)
                
                if (!feature || feature.geometry.type !== 'LineString') {
                    console.warn('Invalid feature:', feature)
                    return
                }

                const converted = ds.convertTerraDrawFeatureToGoogleMapsOverlay(feature)
                
                if (converted) {
                    this.splitLine = converted.overlay
                    this.splitLine.setMap(toRaw(map.map))
                    // Add context menu to split line
                    ds.handleContextMenu(this.splitLine)
                    // Cleanup TerraDraw
                    map.terraDraw.removeFeatures([feature.id])
                    map.terraDraw.setMode('select')
                    map.terraDraw.stop()

                    const coordinates = this.splitLine.getPath().getArray()
                    const geoJsonCoordinates = coordinates.map(coord => [coord.lng(), coord.lat()])
                    this.splitLineLength = this.calculateSplitLineLength(geoJsonCoordinates)
                    this.drawSplitLine = false
                    this.cleanupDrawingListener()
                    
                    // Center map on both field and split line (optional) <------- Disabled for now
                    // this.centerMapOnFieldAndLine()
                } else {
                    console.error('Could not convert feature')
                    map.terraDraw.removeFeatures([feature.id])
                }
            }
            
            map.terraDraw.on('finish', this.terraDrawListener)
        },
        cleanupDrawingListener() {
            const map = useMaps()
            if (this.terraDrawListener && map.terraDraw) {
                map.terraDraw.off('finish', this.terraDrawListener)
                this.terraDrawListener = null
            }
        },
        stopDrawingSplitLine() {
            const map = useMaps()
            if (map.terraDraw && map.terraDraw.enabled) {
                map.terraDraw.setMode('select')
                map.terraDraw.stop()
            }
            this.cleanupDrawingListener()
            this.drawSplitLine = false
        },
        clearPreviousDrawing() {
            if (this.splitLine) {
                toRaw(this.splitLine).setMap(null)
                this.splitLine = null
            }
            // Clear all split polygons from map
            this.splitPolygons.forEach(polygon => {
                if (polygon) {
                    toRaw(polygon).setMap(null)
                }
            })
            this.splitPolygons = []
            this.splitLineLength = 0
            this.polygons = []
            this.polygonAreas = []
            this.selectedPartIndex = null
            this.noSplitedFieldWarning = null
            this.noSplitedFieldWarning = null
            this.selectedPartIndex = null
        },
        drawLineFromResponse(lineGeoJson) {
            const map = useMaps()
            
            if (!lineGeoJson || !lineGeoJson.coordinates || lineGeoJson.coordinates.length < 2) {
                console.error('Invalid line geometry in response')
                return
            }

            // Clear existing split line if any
            if (this.splitLine) {
                toRaw(this.splitLine).setMap(null)
            }

            // Convert coordinates from [lng, lat] to Google Maps format {lat, lng}
            const path = lineGeoJson.coordinates.map(coord => ({
                lat: coord[1],
                lng: coord[0]
            }))

            // Create Google Maps Polyline
            this.splitLine = new map.google.maps.Polyline({
                path: path,
                strokeColor: '#FF0000',
                strokeOpacity: 1.0,
                strokeWeight: 3,
                geodesic: true,
                clickable: false,
                editable: true,
                zIndex: 1000,
                map: toRaw(map.map)
            })

            // Add context menu to split line
            const ds = useDrawingStore()
            ds.handleContextMenu(this.splitLine)

            // Calculate line length
            this.splitLineLength = this.calculateSplitLineLength(lineGeoJson.coordinates)
            
            // Center map on both field and split line (useful when imported from a shapefile and the line is far from the field)
            this.centerMapOnFieldAndLine()
        },
        centerMapOnFieldAndLine() {
            const map = useMaps()
            const ds = useDrawingStore()
            
            if (!map.map || !map.google) {
                return
            }
            
            // Create bounds that will include both field and split line
            const bounds = new map.google.maps.LatLngBounds()
            
            // Add field bounds if available
            if (ds.field.outerBorder && ds.field.outerBorder.coordinates && ds.field.outerBorder.coordinates[0]) {
                const fieldCoords = ds.field.outerBorder.coordinates[0]
                fieldCoords.forEach(coord => {
                    bounds.extend(new map.google.maps.LatLng(coord[1], coord[0]))
                })
            }
            
            // Add split line bounds if available
            if (this.splitLine) {
                const path = this.splitLine.getPath()
                if (path) {
                    const pathArray = path.getArray()
                    pathArray.forEach(point => {
                        bounds.extend(point)
                    })
                }
            }
            
            // Fit map to bounds if we have valid bounds
            if (!bounds.isEmpty()) {
                map.map.fitBounds(bounds)
            }
        },
        calculateSplitLineLength(geoJsonCoordinates) {
            const geojson = {
                type: 'FeatureCollection',
                features: [{
                    type: 'Feature',
                    geometry: { 
                        type: 'LineString', 
                        coordinates: geoJsonCoordinates 
                    }
                }]
            }
            // turf.length() returns length in kilometers by default, convert to meters
            return turf.length(geojson) * 1000
        },
        async getSplitedField() {
            if (!this.splitLine) {
                console.error('Split line not found')
                return null
            }
            
            const ds = useDrawingStore()
            if (!ds.field.outerBorder) {
                console.error('Field outer border not found')
                return null
            }

            // Get split line coordinates in GeoJSON format [lng, lat]
            const splitLineCoords = this.splitLine.getPath().getArray()
                .map(coord => [coord.lng(), coord.lat()])

            // Get outer border coordinates (first ring)
            // outerBorder.coordinates is an array of rings, where [0] is the outer ring
            const outerBorderRing = ds.field.outerBorder.coordinates[0]
            
            // Get cutouts coordinates as interior rings
            const cutoutsCoords = (ds.field.cutouts || []).map(cutout => 
                cutout.getPath().getArray()
                    .map(coord => [coord.lng(), coord.lat()])
            )

            // Build request data with outer border and cutouts:
            // polygon: { type: "Polygon", coordinates: [[outer ring], [cutout1], [cutout2], ...] }
            // line: { type: "LineString", coordinates: [...] }
            const data = {
                polygon: {
                    type: 'Polygon',
                    coordinates: [
                        outerBorderRing,
                        ...cutoutsCoords
                    ]
                },
                line: {
                    type: 'LineString',
                    coordinates: splitLineCoords
                }
            }

            const response = await postSplitField(data)
            if (!response) {
                console.error('Failed to split field')
                return null
            }

            // Store all polygons and calculate areas
            this.polygons = response.polygons || []
            this.polygonAreas = this.polygons.map(polygon => turf.area(polygon))
            
            // Draw polygons on map with colors (green for largest, red for others)
            this.drawSplitPolygonsOnMap()
            
            setTimeout(async () => {
                await this.drawFieldsSVG()
            }, 100)
            return response
        },
        async drawFieldsSVG() {
            const map = useMaps()
            const ds = useDrawingStore()
            
            if (!ds.field) {
                console.error('Field ID not found')
                return
            }

            if (!this.polygons || this.polygons.length === 0) {
                console.error('No polygons to draw')
                return
            }

            // sort the polygons by area
            this.polygons.sort((a, b) => turf.area(b) - turf.area(a))

            // sort the polygonAreas by area
            this.polygonAreas.sort((a, b) => b - a)

            // Use field ID if available, otherwise use 'new' for unsaved fields
            const fieldId = ds.field.id || 'new'
            
            for (let i = 0; i < this.polygons.length; i++) {
                const polygon = this.polygons[i]
                // the biggest polygon should be green, the smallest should be red
                const color = i === 0 ? '#00FF00' : '#FF0000'
                const id = `part-${i + 1}-${fieldId}`
                
                let geoJson = {
                    id: id,
                    type: 'polygon',
                    color: color,
                    outer_border: {
                        type: 'Polygon',
                        coordinates: polygon.coordinates
                    }
                }
                await map.drawGeoSVG(geoJson)
            }
        },
        centerPolygon(polygon) {
            const map = useMaps()
            if (!polygon || !polygon.coordinates || !polygon.coordinates[0]) {
                console.error('Invalid polygon format')
                return
            }
            const bounds = new map.google.maps.LatLngBounds()
            polygon.coordinates[0].forEach(coord => {
                bounds.extend(new map.google.maps.LatLng(coord[1], coord[0]))
            })
            map.map.fitBounds(bounds)
        },
        keepPart(index) {
            if (index < 0 || index >= this.polygons.length) {
                console.error('Invalid part index:', index)
                return
            }
            // Store the selected part index
            this.selectedPartIndex = index
        },
        async downloadShapeFile(index) {
            if (index < 0 || index >= this.polygons.length) {
                console.error('Invalid part index:', index)
                return
            }

            const ds = useDrawingStore()
            const fileName = `field_${ds.field.name}_part_${index + 1}_${moment().format('YYYY-MM-DD-HH-mm')}`
            const feature = {
                type: 'Feature',
                geometry: this.polygons[index],
                properties: {
                    part: index + 1,
                    area: this.polygonAreas[index]
                }
            }
            await downloadFeatureAsShapefile(feature, fileName)
        },
        async uploadShapeFileLine(file) {
            if (!file) {
                console.error('No file selected')
                return null
            }

            const ds = useDrawingStore()
            if (!ds.field.outerBorder) {
                console.error('Field outer border not found')
                return null
            }

            // Get outer border coordinates (first ring)
            // outerBorder.coordinates is an array of rings, where [0] is the outer ring
            const outerBorderRing = ds.field.outerBorder.coordinates[0]
            
            // Get cutouts coordinates as interior rings
            const cutoutsCoords = (ds.field.cutouts || []).map(cutout => 
                cutout.getPath().getArray()
                    .map(coord => [coord.lng(), coord.lat()])
            )

            // Build polygon data with outer border and cutouts:
            // polygon: { type: "Polygon", coordinates: [[outer ring], [cutout1], [cutout2], ...] }
            const polygon = {
                type: 'Polygon',
                coordinates: [
                    outerBorderRing,
                    ...cutoutsCoords
                ]
            }

            const formData = new FormData()
            formData.append('polygon', JSON.stringify(polygon))
            formData.append('file', file)

            const response = await postUploadShapeFileLine(formData)
            if (!response) {
                console.error('Failed to upload shape file line')
                return null
            }

            // Store all polygons and calculate areas
            this.polygons = response.polygons || []
            this.polygonAreas = this.polygons.map(polygon => turf.area(polygon))

            if (this.polygons.length === 1) {
                this.noSplitedFieldWarning = true
            }
            
            // Draw the line from response on the map
            if (response.line && response.line.coordinates) {
                this.drawLineFromResponse(response.line)
            }
            
            // Draw polygons on map with colors (green for largest, red for others)
            this.drawSplitPolygonsOnMap()
            
            setTimeout(async () => {
                await this.drawFieldsSVG()
            }, 100)
            return response
        },
        drawSplitPolygonsOnMap() {
            const map = useMaps()
            
            // Clear existing polygons
            this.splitPolygons.forEach(polygon => {
                if (polygon) {
                    toRaw(polygon).setMap(null)
                }
            })
            this.splitPolygons = []
            
            if (!this.polygons || this.polygons.length === 0) {
                return
            }
            
            // Sort polygons by area (largest first) to determine colors
            const sortedPolygons = [...this.polygons].map((polygon, index) => ({
                polygon,
                area: this.polygonAreas[index],
                originalIndex: index
            })).sort((a, b) => b.area - a.area)
            
            // Draw each polygon on the map
            sortedPolygons.forEach((item, index) => {
                const polygon = item.polygon
                
                if (!polygon || !polygon.coordinates || !polygon.coordinates[0]) {
                    console.warn('Invalid polygon format:', polygon)
                    return
                }
                
                // Convert coordinates from GeoJSON [lng, lat] to Google Maps {lat, lng}
                const paths = polygon.coordinates.map(ring => 
                    ring.map(coord => ({
                        lat: coord[1],
                        lng: coord[0]
                    }))
                )
                
                // Color: green for largest (index 0), red for others
                const color = index === 0 ? '#00FF00' : '#FF0000'
                
                // Create Google Maps Polygon
                const mapPolygon = new map.google.maps.Polygon({
                    paths: paths,
                    strokeColor: color,
                    strokeOpacity: 1.0,
                    strokeWeight: 3,
                    fillColor: color,
                    fillOpacity: 0.3,
                    clickable: false,
                    zIndex: 50,
                    map: toRaw(map.map)
                })
                
                this.splitPolygons.push(mapPolygon)
            })
        },
        async continueToEdit() {
            // Continue to edit field and replace the field (from drawing store) with the selected part as outer border
            const ds = useDrawingStore()
            const map = useMaps()
            
            if (this.selectedPartIndex === null || this.selectedPartIndex < 0 || this.selectedPartIndex >= this.polygons.length) {
                console.error('No part selected or invalid index')
                return
            }
            
            const selectedPolygon = this.polygons[this.selectedPartIndex]
            if (!selectedPolygon || !selectedPolygon.coordinates || !selectedPolygon.coordinates[0]) {
                console.error('Invalid polygon format')
                return
            }
            
            // Extract outer border coordinates (first ring)
            const outerBorderCoords = selectedPolygon.coordinates[0]
            
            // Extract cutouts coordinates (interior rings, if any)
            const cutoutsCoords = selectedPolygon.coordinates.slice(1)
            
            // Clear existing field border
            ds.field.shapes.forEach(shape => {
                if (shape) {
                    toRaw(shape).setMap(null)
                    ds.removeHandleUpdateListeners(shape)
                }
            })
            ds.field.shapes = []
            
            // Clear existing cutouts
            ds.field.cutouts.forEach(cutout => {
                if (cutout) {
                    toRaw(cutout).setMap(null)
                    ds.removeHandleUpdateListeners(cutout)
                }
            })
            ds.field.cutouts = []
            
            // Create new outer border polygon with original coordinates (same as uploadFieldFile logic)
            const outerBorderPath = geoJsonRingToOpenPath(outerBorderCoords)
            
            const outerBorderPolygon = new map.google.maps.Polygon({
                paths: outerBorderPath,
                editable: true,
                strokeColor: 'red',
                strokeWeight: 2,
                fillColor: 'transparent',
                fillOpacity: 0,
                zIndex: 1
            })
            
            ds.field.shapes = [outerBorderPolygon]
            outerBorderPolygon.setMap(toRaw(map.map))
            ds.handleContextMenu(outerBorderPolygon)
            ds.addHandleUpdateListeners(outerBorderPolygon)
            
            // Create cutouts using handleObstacleDrawing function
            const rewindedCutouts = []
            for (let i = 0; i < cutoutsCoords.length; i++) {
                const cutoutPath = geoJsonRingToOpenPath(cutoutsCoords[i])
                
                // Create temporary polygon for handleObstacleDrawing
                const cutoutPolygon = new map.google.maps.Polygon({
                    paths: cutoutPath,
                    editable: true
                })

                const success = await ds.handleObstacleDrawing(cutoutPolygon)
                
                if (success) {
                    // Get the rewinded coordinates
                    const processedPath = cutoutPolygon.getPath().getArray()
                    const processedCoords = processedPath.map(point => [point.lng(), point.lat()])
                    rewindedCutouts.push(processedCoords)
                } else {
                    // If validation failed, remove the polygon
                    cutoutPolygon.setMap(null)
                }
            }
            
            // Set outerBorder with rewinded coordinates (same as uploadFieldFile logic)
            const rewindedOuterBorder = ds.rewindRing([...outerBorderCoords], true)
            ds.field.outerBorder = {
                type: 'Polygon',
                coordinates: [rewindedOuterBorder, ...rewindedCutouts]
            }
            
            // Update flags
            ds.outerBorderDrawed = true
            ds.isStartingDrawing = true
            
            const resGenHeadlands = await ds.generateHeadlands()
            if (resGenHeadlands) {
                if (ds.field.id) {
                    await ds.generateABLines('originalABlines')
                }
                await ds.updateGeneratedABlines()
                await ds.optimizeLongestEdge()
            }

            // Calculate the new field area
            ds.field.fieldArea = ds.calculateFieldArea()
            
            // Clear split field state and close split field component
            this.isSplitting = false
            this.clearPreviousDrawing()
            this.stopDrawingSplitLine()
        },
        isPartTooSmall(index) {
            // 20000 square meters is the minimum area for headlands and AB lines (HARDCODED but safe enough for now)
            return this.polygonAreas[index] < 20000
        },
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useSplitFieldStore, import.meta.hot))
}
